/**
 * Absolute-session-lifetime policy, client side (ADR-0017). The BACKEND is the
 * enforcement point (jwt.strategy rejects tokens whose Firebase `auth_time` is
 * older than SESSION_MAX_AGE_HOURS with 401 {code:'SESSION_EXPIRED'}); this
 * module only powers the PROACTIVE re-auth UX — warn shortly before the
 * deadline, lock in place after it — so the vet re-enters their password
 * without ever being signed out (no replica wipe, no /login bounce).
 *
 * Same design rules as offlineAuthGrace: pure, dependency-free decision logic
 * plus the small localStorage state it needs — no React/Firebase imports, and
 * every garbage input fails SAFE to "no deadline" (a bad clock or a corrupt
 * cache must never lock a vet out; the backend gate stays authoritative).
 */

/** How long before the deadline the proactive warning engages. */
export const SESSION_WARN_WINDOW_MS = 30 * 60 * 1000; // 30 min

const POLICY_KEY = 'st.sessionPolicy.maxAgeHours';

export type SessionExpiryPhase = 'ok' | 'warn' | 'expired';

/** Cache the server-reported limit (GET /public/auth-config) so the UX keeps
 *  working offline / before the next fetch. `null` = gate disabled. */
export function cacheSessionPolicyHours(hours: number | null): void {
  try {
    if (hours == null) localStorage.removeItem(POLICY_KEY);
    else localStorage.setItem(POLICY_KEY, String(hours));
  } catch {
    /* localStorage unavailable — proactive UX simply won't engage; fail safe */
  }
}

export function getCachedSessionPolicyHours(): number | null {
  try {
    const raw = localStorage.getItem(POLICY_KEY);
    if (raw == null) return null;
    const hours = Number(raw);
    return Number.isFinite(hours) && hours > 0 ? hours : null;
  } catch {
    return null;
  }
}

/**
 * The ms timestamp after which the backend will reject this session's tokens,
 * from the Firebase ID-token result's `authTime` (ISO/UTC string — stamped at
 * genuine sign-in only, silent refreshes don't move it). Null = no deadline
 * (gate disabled, or unparseable input → fail safe).
 */
export function computeSessionDeadline(
  authTimeIso: string | null | undefined,
  maxAgeHours: number | null,
): number | null {
  if (maxAgeHours == null || !Number.isFinite(maxAgeHours) || maxAgeHours <= 0) return null;
  if (!authTimeIso) return null;
  const authTimeMs = Date.parse(authTimeIso);
  if (Number.isNaN(authTimeMs)) return null;
  return authTimeMs + maxAgeHours * 3600 * 1000;
}

/** The claim a workstation PIN unlock's custom token carries: the last PASSWORD sign-in (epoch s). */
export const PASSWORD_AUTH_TIME_CLAIM = 'pwd_auth_time';

/**
 * When this session last proved a password, from its ID token result, or null (no deadline). A
 * custom-token session (a PIN unlock as someone else) answers from PASSWORD_AUTH_TIME_CLAIM, so the
 * unlock never restarts the clock; any other provider answers from `authTime`. Mirrors the server's
 * `passwordAuthTimeOf` (backend/src/auth/session-policy.ts).
 */
export function passwordAuthTimeIso(result: {
  authTime?: string | null;
  signInProvider?: string | null;
  claims?: Record<string, unknown>;
}): string | null {
  if (result.signInProvider !== 'custom') return result.authTime || null;
  const seconds = result.claims?.[PASSWORD_AUTH_TIME_CLAIM];
  return typeof seconds === 'number' && Number.isFinite(seconds)
    ? new Date(seconds * 1000).toISOString()
    : null;
}

/**
 * The API's "your 24 hours are up" refusal (401 {code:'SESSION_EXPIRED'}). It is never a verdict
 * about the user: the re-auth dialog resolves it, and a check that deferred on it re-runs once the
 * password is accepted (AuthContext.reauthCount).
 */
export function isSessionExpiredError(error: unknown): boolean {
  const res = (error as { response?: { status?: number; data?: { code?: unknown } } } | null)
    ?.response;
  return res?.status === 401 && res.data?.code === 'SESSION_EXPIRED';
}

/** Where this session stands relative to its deadline. Garbage → 'ok'. */
export function classifySessionExpiry(deadline: number | null, now: number): SessionExpiryPhase {
  if (deadline == null || !Number.isFinite(deadline) || !Number.isFinite(now)) return 'ok';
  if (now >= deadline) return 'expired';
  if (now >= deadline - SESSION_WARN_WINDOW_MS) return 'warn';
  return 'ok';
}
